// Designer op model — client side (Pilar F, F1).
//
// Mirrors the server op types (services/mcp-gateway/src/designer). The
// client only needs FORWARD apply (optimistic local update); undo/redo
// replay the inverse ops the server returns from apply_ops, so no inverse
// computation is duplicated here. (A shared package is the eventual home;
// duplicated for now — the types are small + stable.)

export type NodeKind = 'frame' | 'group' | 'panel' | 'shape' | 'text' | 'embed';

export interface GridPlacement { x: number; y: number; w: number; h: number }
export interface RectPlacement { x: number; y: number; w: number; h: number; rotation?: number }

export interface DesignerNode {
  id: string;
  kind: NodeKind;
  name?: string;
  grid?: GridPlacement;
  rect?: RectPlacement;
  props?: Record<string, unknown>;
  dataBinding?: unknown;
  states?: unknown[];
  events?: Record<string, unknown[]>;
  animations?: unknown[];
  children?: DesignerNode[];
}

export interface TemplateVariableLite { name: string; type: string; [k: string]: unknown }

export interface DesignerDocument {
  themeTokens?: Record<string, string>;
  id: string;
  kind: 'dashboard' | 'design';
  variables: TemplateVariableLite[];
  root: DesignerNode;
  rev: number;
}

export type DesignerOp =
  | { t: 'add_node'; parentId: string; index?: number; node: DesignerNode }
  | { t: 'remove_node'; id: string }
  | { t: 'update_node'; id: string; patch: { name?: string | null; grid?: GridPlacement | null; rect?: RectPlacement | null; dataBinding?: unknown; props?: Record<string, unknown>; states?: unknown[] | null; events?: Record<string, unknown[]> | null; animations?: unknown[] | null } }
  | { t: 'move_node'; id: string; parentId: string; index: number }
  | { t: 'set_variable'; variable: TemplateVariableLite }
  | { t: 'remove_variable'; name: string };

function clone<T>(v: T): T {
  return JSON.parse(JSON.stringify(v));
}

export function locate(doc: DesignerDocument, id: string): { node: DesignerNode; parent: DesignerNode | null; index: number } | null {
  if (doc.root.id === id) return { node: doc.root, parent: null, index: -1 };
  const stack: Array<{ node: DesignerNode; parent: DesignerNode }> = [];
  for (const c of doc.root.children ?? []) stack.push({ node: c, parent: doc.root });
  while (stack.length) {
    const { node, parent } = stack.pop()!;
    if (node.id === id) return { node, parent, index: (parent.children ?? []).indexOf(node) };
    for (const c of node.children ?? []) stack.push({ node: c, parent: node });
  }
  return null;
}

/** Forward-apply a batch to a COPY of the document (optimistic local). */
export function applyOps(input: DesignerDocument, ops: DesignerOp[]): DesignerDocument {
  const doc = clone(input);
  for (const op of ops) applyOne(doc, op);
  return doc;
}

function applyOne(doc: DesignerDocument, op: DesignerOp): void {
  switch (op.t) {
    case 'add_node': {
      const parent = locate(doc, op.parentId)?.node;
      if (!parent) return;
      parent.children ??= [];
      parent.children.splice(op.index ?? parent.children.length, 0, clone(op.node));
      return;
    }
    case 'remove_node': {
      const t = locate(doc, op.id);
      if (t?.parent) t.parent.children!.splice(t.index, 1);
      return;
    }
    case 'update_node': {
      const found = locate(doc, op.id)?.node;
      if (!found) return;
      const node = found as unknown as Record<string, unknown>;
      const patch = op.patch as Record<string, unknown>;
      // Whole-value fields: null deletes, undefined skips, else replace.
      for (const key of ['name', 'grid', 'rect', 'dataBinding', 'states', 'events', 'animations'] as const) {
        if (patch[key] === undefined) continue;
        if (patch[key] === null) delete node[key];
        else node[key] = clone(patch[key]);
      }
      if (op.patch.props !== undefined) {
        const props = (node.props ??= {}) as Record<string, unknown>;
        for (const [k, v] of Object.entries(op.patch.props)) {
          if (v === undefined || v === null) delete props[k];
          else props[k] = clone(v);
        }
      }
      return;
    }
    case 'move_node': {
      const t = locate(doc, op.id);
      const dest = locate(doc, op.parentId)?.node;
      if (!t?.parent || !dest) return;
      const [moved] = t.parent.children!.splice(t.index, 1);
      dest.children ??= [];
      dest.children.splice(op.index, 0, moved!);
      return;
    }
    case 'set_variable': {
      const i = doc.variables.findIndex((v) => v.name === op.variable.name);
      if (i >= 0) doc.variables[i] = clone(op.variable);
      else doc.variables.push(clone(op.variable));
      return;
    }
    case 'remove_variable': {
      const i = doc.variables.findIndex((v) => v.name === op.name);
      if (i >= 0) doc.variables.splice(i, 1);
      return;
    }
  }
}

let counter = 0;
/** Client-side unique node id (server accepts any unique id). */
export function newNodeId(prefix = 'nd'): string {
  counter += 1;
  return `${prefix}_${Date.now().toString(36)}${counter.toString(36)}`;
}
